import {
  isEndingThisPeriod,
  isNeverRenewed,
  type SubscriptionEndFields,
} from '@/helpers/subscription';

export const formatStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    PAUSED: 'Paused',
    EXPIRED: 'Expired',
    PAST_DUE: 'Past due',
    CANCELED: 'Canceled',
    DELETED: 'Deleted',
    FULLY_USED: 'No more credits',
  };
  return statusMap[status] ?? 'Unknown';
};

export const getStatusBadgeColor = (
  status: string
): 'green' | 'yellow' | 'red' | 'gray' => {
  switch (status) {
    case 'ACTIVE':
      return 'green';
    case 'PAUSED':
      return 'yellow';
    case 'CANCELED':
    case 'EXPIRED':
    case 'PAST_DUE':
    case 'FULLY_USED':
      return 'red';
    default:
      return 'gray';
  }
};

export const getStatusTextClass = (status: string): string => {
  switch (status) {
    case 'ACTIVE':
      return 'text-green-600';
    case 'PAUSED':
      return 'text-yellow-600';
    case 'CANCELED':
    case 'EXPIRED':
    case 'PAST_DUE':
    case 'FULLY_USED':
      return 'text-error-600';
    default:
      return 'text-gray-900';
  }
};

/**
 * Returns the appropriate label for a subscription's period end date.
 *
 * - EXPIRED/DELETED, and PAST_DUE (period end is always in past) → "Expired"
 * - Last payment in, period still running → "Ends" (it runs out as sold)
 * - PAUSED/CANCELED, or a cancel on it → "Expires", or "Expired" once past
 * - Bought for nothing → null: nothing renews it
 * - Otherwise → "Renews"
 */
export const getSubscriptionRenewalLabel = (
  code: SubscriptionEndFields,
  now: Date = new Date()
): 'Renews' | 'Ends' | 'Expires' | 'Expired' | null => {
  const { status, canceled_at, current_period_end } = code;
  if (status === 'EXPIRED' || status === 'DELETED' || status === 'PAST_DUE') {
    return 'Expired';
  }
  if (isEndingThisPeriod(code, now)) return 'Ends';
  if (status === 'CANCELED' || status === 'PAUSED' || canceled_at) {
    return current_period_end && new Date(current_period_end) < now
      ? 'Expired'
      : 'Expires';
  }
  if (isNeverRenewed(code)) return null;
  return 'Renews';
};
